import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchCities } from '../../api/citiesApi'
import { fetchLanguagesConfig } from '../../api/languagesApi'
import styles from './LanguagePickerDropdown.module.css'

interface LanguagePickerDropdownProps {
  currentLang: string
  currentCity: string
  onSelect: (langCode: string) => void
}

export default function LanguagePickerDropdown({
  currentLang,
  currentCity,
  onSelect,
}: LanguagePickerDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [btnRect, setBtnRect] = useState<DOMRect | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => fetchCities(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: allLanguages } = useQuery({
    queryKey: ['languages-config'],
    queryFn: fetchLanguagesConfig,
    staleTime: 60 * 60 * 1000,
  })

  // Languages that exist in at least one city's output_languages
  const availableLanguageCodes = new Set<string>(
    (cities ?? []).flatMap((c) => c.output_languages),
  )

  // Languages to show in dropdown
  const displayLanguages = (allLanguages ?? []).filter((l) =>
    availableLanguageCodes.has(l.code),
  )

  // Languages the current city supports
  const currentCityOutputLangs = new Set<string>(
    cities?.find((c) => c.slug === currentCity)?.output_languages ?? [],
  )

  const currentFlag =
    allLanguages?.find((l) => l.code === currentLang)?.flag ?? currentLang

  // Current city name for tooltip
  const currentCityName =
    cities?.find((c) => c.slug === currentCity)?.name ?? currentCity

  // Close on click-outside (button or portal'd dropdown)
  useEffect(() => {
    if (!open) return
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node
      if (!btnRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  // Close on any scroll (including horizontal scroll of the menu bar)
  useEffect(() => {
    if (!open) return
    function handleScroll() { setOpen(false) }
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', handleScroll, { capture: true })
  }, [open])

  function handleToggle() {
    setOpen((v) => {
      if (!v && btnRef.current) {
        setBtnRect(btnRef.current.getBoundingClientRect())
      }
      return !v
    })
  }

  function handleSelect(code: string) {
    setOpen(false)
    onSelect(code)
  }

  return (
    <div className={styles.wrapper}>
      <button
        ref={btnRef}
        type="button"
        className={styles.btn}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('news_language')}
      >
        {currentFlag}
      </button>

      {open && btnRect && createPortal(
        <ul
          ref={dropdownRef}
          className={styles.dropdown}
          role="listbox"
          aria-label={t('news_language')}
          style={{ top: btnRect.bottom + 4, left: btnRect.left }}
        >
          {displayLanguages.map((lang) => {
            const available = currentCityOutputLangs.size === 0 || currentCityOutputLangs.has(lang.code)
            const unavailableTitle = available
              ? undefined
              : t('language_unavailable_tooltip', {
                  city: currentCityName,
                  langs: [...currentCityOutputLangs].join(', '),
                })
            return (
              <li key={lang.code} role="option" aria-selected={lang.code === currentLang}>
                <button
                  type="button"
                  className={`${styles.item} ${lang.code === currentLang ? styles.itemActive : ''} ${!available ? styles.itemDisabled : ''}`}
                  onClick={available ? () => handleSelect(lang.code) : undefined}
                  disabled={!available}
                  title={unavailableTitle}
                >
                  {lang.code} {lang.flag}
                </button>
              </li>
            )
          })}
        </ul>,
        document.body,
      )}
    </div>
  )
}
